// https://github.com/orestbida/cookieconsent
// https://github.com/orestbida/cookieconsent/tree/v2.9
import "vanilla-cookieconsent";
import prestashop from "prestashop";
import { merge } from "lodash-es";
import { ready } from "@front/utils";

type TCookieReadonly = boolean;
type TCookieEnabled = boolean;

type TCookies = {
    [key: string]: [TCookieReadonly, TCookieEnabled];
};

const { rgpd: i18n } = prestashop.i18n;

const makeCmsBlock = () => ({});

const makeCookieBlock = (
    category: string,
    readonly = false,
    enabled = true
) => ({
    toggle: {
        value: category,
        readonly,
        enabled,
    },
});

const makeCookieBlocks = (cookies: TCookies) =>
    Object.entries(cookies).map(([value, [readonly, enabled]]) =>
        makeCookieBlock(value, readonly, enabled)
    );

const cookies: TCookies = {
    functionality: [true, true],
    analytics: [false, true],
    ad: [false, true],
    rolex: [false, true],
};

const makeLang = (iso_code: string) => ({
    [iso_code]: merge(i18n, {
        consent_modal: {
            primary_btn: {
                role: "accept_all",
            },
            secondary_btn: {
                role: "accept_necessary",
            },
        },
        settings_modal: {
            blocks: [
                makeCmsBlock(),
                ...makeCookieBlocks(cookies),
                makeCmsBlock(),
            ],
        },
    }),
});

const capitalize = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1);

const initializeConsent = () => {
    Object.entries(cookies).forEach(([c, [ro, e]]) => {
        window.dataLayer.push({
            event: `gdpr${ro && e ? "Allow" : "Disallow"}${capitalize(c)}`,
            [`gdprAllow${capitalize(c)}`]: ro && e,
        });
    });
};

const updateConsent = () => {
    const { accepted_categories, rejected_categories } =
        cc.getUserPreferences();

    accepted_categories.forEach(category => {
        window.dataLayer.push({
            event: `gdprAllow${capitalize(category)}`,
            [`gdprAllow${capitalize(category)}`]: true,
        });
    });

    rejected_categories.forEach(category => {
        window.dataLayer.push({
            event: `gdprDisallow${capitalize(category)}`,
            [`gdprAllow${capitalize(category)}`]: false,
        });
    });

    window.dispatchEvent(new CustomEvent('CookieConsent:updateConsent', { detail: { cookieConsent: cc } }));
};

const ccConfig: UserConfig = {
    revision: 0,
    current_lang: prestashop.language.iso_code,
    cookie_name: "lepage_cookie",
    page_scripts: true,
    mode: "opt-in",

    onAccept() {
        updateConsent();
        window.dataLayer.push({ event: "gdprInitialized" });
    },

    onChange() {
        updateConsent();
    },

    gui_options: {
        consent_modal: {
            layout: "cloud",
            transition: "slide",
        },
        settings_modal: {
            layout: "bar",
            position: "right",
            transition: "slide",
        },
    },

    languages: {
        ...makeLang("fr"),
        ...makeLang("en"),
    },
};

const cc = initCookieConsent();

ready(() => {
    cc.run(ccConfig);

    const { rejected_categories } = cc.getUserPreferences();
    const mandatory_categories = Object.entries(cookies)
        .filter(([c, [ro]]) => ro)
        .map(([c]) => c);

    if (mandatory_categories.some(r => rejected_categories.includes(r))) {
        initializeConsent();
    }
});
